/**
 * Data layer for `bot-traffic-detection`.
 * Raw fetch() confined to this file per dash contract.
 *
 * Backend stores generic event rows with payload_json. FE decodes payload_json
 * to extract bot-traffic fields: ip, ua, score, reason.
 */

export interface BotTrafficEventRow {
	id: number;
	tenant_id: string;
	event_id: string;
	payload_json: string;
	created_at: number;
}

export interface ListBotTrafficResponse {
	items: BotTrafficEventRow[];
}

export interface BotTrafficPayload {
	ip?: string;
	ua?: string;
	score?: number;
	reason?: string;
	[key: string]: unknown;
}

export interface DecodedBotEvent {
	id: number;
	event_id: string;
	created_at: number;
	ip: string;
	ua: string;
	score: number;
	reason: string;
}

export interface CreateBotTrafficBody {
	event_id: string;
	ip?: string;
	ua?: string;
	score?: number;
	reason?: string;
}

const BASE = "/api/v1/bot-traffic-detection";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListBotTrafficResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBotTrafficResponse>(res);
}

export async function create(body: CreateBotTrafficBody): Promise<{ ok: boolean; event_id: string; created_at: number }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; event_id: string; created_at: number }>(res);
}

export const botTrafficDetectionQueryKeys = {
	all: ["bot-traffic-detection"] as const,
	list: () => [...botTrafficDetectionQueryKeys.all] as const,
	detail: (id: string) => [...botTrafficDetectionQueryKeys.all, id] as const,
};

/**
 * Decode payload_json into structured fields; fall back to safe defaults.
 */
export function decodeEvent(row: BotTrafficEventRow): DecodedBotEvent {
	let payload: BotTrafficPayload = {};
	try {
		const parsed = JSON.parse(row.payload_json);
		if (parsed && typeof parsed === "object") payload = parsed as BotTrafficPayload;
	} catch {
		payload = {};
	}
	const scoreRaw = typeof payload.score === "number" ? payload.score : Number(payload.score);
	const score = Number.isFinite(scoreRaw) ? Math.max(0, Math.min(1, scoreRaw)) : 0;
	return {
		id: row.id,
		event_id: row.event_id,
		created_at: row.created_at,
		ip: typeof payload.ip === "string" ? payload.ip : "-",
		ua: typeof payload.ua === "string" ? payload.ua : "-",
		score,
		reason: typeof payload.reason === "string" ? payload.reason : "-",
	};
}

/**
 * Bucket scores into 10 bins (0.0-0.1, 0.1-0.2, ..., 0.9-1.0).
 * Returns array of 10 counts; index 0 = scores in [0, 0.1), index 9 = [0.9, 1.0].
 */
export function bucketScores(events: DecodedBotEvent[]): number[] {
	const buckets = new Array(10).fill(0) as number[];
	for (const e of events) {
		const idx = e.score >= 1 ? 9 : Math.floor(e.score * 10);
		buckets[idx] += 1;
	}
	return buckets;
}

/**
 * Apply sensitivity threshold: only events with score >= threshold are "flagged".
 */
export function flaggedByThreshold(events: DecodedBotEvent[], threshold: number): DecodedBotEvent[] {
	return events.filter((e) => e.score >= threshold);
}
